import { z } from 'zod';
import {
  adminAssetListSchema,
  adminStatsSchema,
  adminActivitySchema,
  type HeartbeatRequest,
  adminProjectListSchema,
  adminUserListSchema,
  adminUserSchema,
  auditListSchema,
  versionListResponseSchema,
  versionSummarySchema,
  type UpdateMetaRequest,
  type UpdateUserRequest,
  assetListResponseSchema,
  assetResponseSchema,
  assetUsageResponseSchema,
  authOptionsSchema,
  imageResponseSchema,
  meResponseSchema,
  registerResponseSchema,
  userPasswordResponseSchema,
  type ChangePasswordRequest,
  type CreateUserRequest,
  projectListResponseSchema,
  projectMembersResponseSchema,
  projectResponseSchema,
  type MemberRole,
  type CreateProjectRequest,
  type LoginRequest,
  type RegisterRequest,
  type ReportRequest,
  type UpdateMeRequest,
  videoListResponseSchema,
  videoResponseSchema,
  priceCatalogListSchema,
  priceCatalogSchema,
  type SavePriceCatalogRequest,
  callOptionsResponseSchema,
  pruneVersionsResponseSchema,
  projectPresenceResponseSchema,
  dictationResponseSchema,
  chatListResponseSchema,
  chatMessageResponseSchema,
  interpreterSessionSchema,
  type ChatView,
  type InterpreterRequest,
  type Locale,
} from '@indusketch/shared';
import type { ProjectDocument } from '@indusketch/project-model';
import { apiRequest, apiRequestBlob, apiUpload } from './client.js';

export const authApi = {
  options: () => apiRequest('GET', '/api/auth/options', authOptionsSchema),
  me: () => apiRequest('GET', '/api/auth/me', meResponseSchema),
  login: (input: LoginRequest) => apiRequest('POST', '/api/auth/login', meResponseSchema, input),
  register: (input: RegisterRequest) =>
    apiRequest('POST', '/api/auth/register', registerResponseSchema, input),
  checkAccessCode: (code: string) => apiRequest('POST', '/api/auth/access-code', null, { code }),
  logout: () => apiRequest('POST', '/api/auth/logout', null),
  changePassword: (input: ChangePasswordRequest) =>
    apiRequest('POST', '/api/auth/password', null, input),
  updateMe: (input: UpdateMeRequest) =>
    apiRequest('PATCH', '/api/auth/me', meResponseSchema, input),
};

export const assetsApi = {
  list: () => apiRequest('GET', '/api/assets', assetListResponseSchema),
  upload: (file: File) => {
    const form = new FormData();
    form.append('file', file, file.name);
    return apiRequest('POST', '/api/assets', assetResponseSchema, form);
  },
  /** `projectId`: the project the image is for (an administrator's upload goes to its owner). */
  uploadImage: (file: File, projectId?: string | null) => {
    const form = new FormData();
    form.append('file', file, file.name);
    const query = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
    return apiRequest('POST', `/api/assets/images${query}`, imageResponseSchema, form);
  },
  remove: (id: string) => apiRequest('DELETE', `/api/assets/${encodeURIComponent(id)}`, null),
  usage: (id: string) =>
    apiRequest('GET', `/api/assets/${encodeURIComponent(id)}/usage`, assetUsageResponseSchema),
  rename: (id: string, name: string) =>
    apiRequest('PATCH', `/api/assets/${encodeURIComponent(id)}`, assetResponseSchema, { name }),
  fileUrl: (id: string) => `/api/assets/${encodeURIComponent(id)}/file`,
};

const videosPath = (projectId: string) => `/api/projects/${encodeURIComponent(projectId)}/videos`;

export const videosApi = {
  list: (projectId: string) => apiRequest('GET', videosPath(projectId), videoListResponseSchema),
  upload: (
    projectId: string,
    video: { blob: Blob; name: string; durationMs: number; withSound: boolean },
    onProgress: (fraction: number) => void,
  ) => {
    // Fields before the file: the server reads them while the file streams in.
    const form = new FormData();
    form.append('durationMs', String(Math.round(video.durationMs)));
    form.append('withSound', String(video.withSound));
    form.append('file', video.blob, video.name);
    return apiUpload(videosPath(projectId), form, videoResponseSchema, onProgress);
  },
  rename: (projectId: string, id: string, name: string) =>
    apiRequest('PATCH', `${videosPath(projectId)}/${encodeURIComponent(id)}`, videoResponseSchema, {
      name,
    }),
  remove: (projectId: string, id: string) =>
    apiRequest('DELETE', `${videosPath(projectId)}/${encodeURIComponent(id)}`, null),
  fileUrl: (projectId: string, id: string, download = false) =>
    `${videosPath(projectId)}/${encodeURIComponent(id)}/file${download ? '?download=1' : ''}`,
};

const chatPath = (projectId: string) => `/api/projects/${encodeURIComponent(projectId)}/chat`;
const callPath = (projectId: string) => `/api/projects/${encodeURIComponent(projectId)}/call`;

export const chatApi = {
  list: (projectId: string, before?: string) =>
    apiRequest(
      'GET',
      `${chatPath(projectId)}${before ? `?before=${encodeURIComponent(before)}` : ''}`,
      chatListResponseSchema,
    ),
  send: (projectId: string, text: string) =>
    apiRequest('POST', chatPath(projectId), chatMessageResponseSchema, { text }),
  upload: (
    projectId: string,
    file: Blob,
    fileName: string,
    extra: { caption?: string; view?: ChatView },
    onProgress: (fraction: number) => void,
  ) => {
    // Fields before the file: the server reads them while the file streams in.
    const form = new FormData();
    if (extra.caption) form.append('caption', extra.caption);
    if (extra.view) form.append('view', JSON.stringify(extra.view));
    form.append('file', file, fileName);
    return apiUpload(`${chatPath(projectId)}/files`, form, chatMessageResponseSchema, onProgress);
  },
  read: (projectId: string, at: string) =>
    apiRequest('POST', `${chatPath(projectId)}/read`, null, { at }),
  remove: (projectId: string, messageId: string) =>
    apiRequest('DELETE', `${chatPath(projectId)}/${encodeURIComponent(messageId)}`, null),
  callOptions: (projectId: string) =>
    apiRequest('GET', `${callPath(projectId)}/options`, callOptionsResponseSchema),
  interpreter: (projectId: string, input: InterpreterRequest) =>
    apiRequest('POST', `${callPath(projectId)}/interpreter`, interpreterSessionSchema, input),
  /** Spoken text back as text; the language field goes before the file. */
  transcribe: (projectId: string, wav: Blob, language: Locale) => {
    const form = new FormData();
    form.append('language', language);
    form.append('file', wav, 'diktat.wav');
    return apiRequest('POST', `${chatPath(projectId)}/transcribe`, dictationResponseSchema, form);
  },
};

export const activityApi = {
  heartbeat: (input: HeartbeatRequest) =>
    apiRequest('POST', '/api/activity/heartbeat', null, input),
};

export const adminApi = {
  users: () => apiRequest('GET', '/api/admin/users', adminUserListSchema),
  updateUser: (id: string, patch: UpdateUserRequest) =>
    apiRequest(
      'PATCH',
      `/api/admin/users/${encodeURIComponent(id)}`,
      z.object({ user: adminUserSchema }),
      patch,
    ),
  createUser: (input: CreateUserRequest) =>
    apiRequest('POST', '/api/admin/users', userPasswordResponseSchema, input),
  resetPassword: (id: string) =>
    apiRequest(
      'POST',
      `/api/admin/users/${encodeURIComponent(id)}/password`,
      userPasswordResponseSchema,
    ),
  rejectUser: (id: string) =>
    apiRequest('DELETE', `/api/admin/users/${encodeURIComponent(id)}`, null),
  projects: () => apiRequest('GET', '/api/admin/projects', adminProjectListSchema),
  assets: () => apiRequest('GET', '/api/admin/assets', adminAssetListSchema),
  stats: () => apiRequest('GET', '/api/admin/stats', adminStatsSchema),
  activity: () => apiRequest('GET', '/api/admin/activity', adminActivitySchema),
  uploadSystemAsset: (file: File) => {
    const form = new FormData();
    form.append('file', file, file.name);
    return apiRequest('POST', '/api/admin/assets', assetResponseSchema, form);
  },
  audit: (after?: string) =>
    apiRequest(
      'GET',
      `/api/admin/audit${after ? `?after=${encodeURIComponent(after)}` : ''}`,
      auditListSchema,
    ),
  createPriceCatalog: (input: SavePriceCatalogRequest) =>
    apiRequest('POST', '/api/admin/price-catalogs', priceCatalogResponseSchema, input),
  updatePriceCatalog: (id: string, input: SavePriceCatalogRequest) =>
    apiRequest(
      'PUT',
      `/api/admin/price-catalogs/${encodeURIComponent(id)}`,
      priceCatalogResponseSchema,
      input,
    ),
  deletePriceCatalog: (id: string) =>
    apiRequest('DELETE', `/api/admin/price-catalogs/${encodeURIComponent(id)}`, null),
};

const priceCatalogResponseSchema = z.object({ catalog: priceCatalogSchema });

export const pricesApi = {
  list: () => apiRequest('GET', '/api/price-catalogs', priceCatalogListSchema),
};

export const projectsApi = {
  list: () => apiRequest('GET', '/api/projects', projectListResponseSchema),
  get: (id: string) =>
    apiRequest('GET', `/api/projects/${encodeURIComponent(id)}`, projectResponseSchema),
  create: (input: CreateProjectRequest) =>
    apiRequest('POST', '/api/projects', projectResponseSchema, input),
  save: (id: string, baseRevision: number, document: ProjectDocument) =>
    apiRequest('PUT', `/api/projects/${encodeURIComponent(id)}`, projectResponseSchema, {
      baseRevision,
      document,
    }),
  remove: (id: string) => apiRequest('DELETE', `/api/projects/${encodeURIComponent(id)}`, null),
  presence: (id: string) =>
    apiRequest(
      'GET',
      `/api/projects/${encodeURIComponent(id)}/presence`,
      projectPresenceResponseSchema,
    ),
  report: (id: string, request: ReportRequest) =>
    apiRequestBlob(`/api/projects/${encodeURIComponent(id)}/report`, request),
  saveThumbnail: (id: string, dataUrl: string) =>
    apiRequest('PUT', `/api/projects/${encodeURIComponent(id)}/thumbnail`, null, { dataUrl }),
  updateMeta: (id: string, meta: UpdateMetaRequest) =>
    apiRequest(
      'PATCH',
      `/api/projects/${encodeURIComponent(id)}/meta`,
      projectResponseSchema,
      meta,
    ),
  fork: (id: string, name: string, document: ProjectDocument) =>
    apiRequest('POST', `/api/projects/${encodeURIComponent(id)}/fork`, projectResponseSchema, {
      name,
      document,
    }),
  versions: (id: string) =>
    apiRequest(
      'GET',
      `/api/projects/${encodeURIComponent(id)}/versions`,
      versionListResponseSchema,
    ),
  createVersion: (id: string, label: string) =>
    apiRequest(
      'POST',
      `/api/projects/${encodeURIComponent(id)}/versions`,
      z.object({ version: versionSummarySchema }),
      { label },
    ),
  restoreVersion: (id: string, versionId: string, baseRevision: number) =>
    apiRequest(
      'POST',
      `/api/projects/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/restore`,
      projectResponseSchema,
      { baseRevision },
    ),
  deleteVersion: (id: string, versionId: string) =>
    apiRequest(
      'DELETE',
      `/api/projects/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}`,
      null,
    ),
  pruneVersions: (id: string, olderThanDays: number) =>
    apiRequest(
      'POST',
      `/api/projects/${encodeURIComponent(id)}/versions/prune`,
      pruneVersionsResponseSchema,
      { olderThanDays },
    ),
  duplicate: (id: string, name: string) =>
    apiRequest('POST', `/api/projects/${encodeURIComponent(id)}/duplicate`, projectResponseSchema, {
      name,
    }),
  members: (id: string) =>
    apiRequest(
      'GET',
      `/api/projects/${encodeURIComponent(id)}/members`,
      projectMembersResponseSchema,
    ),
  addMember: (id: string, email: string, role: MemberRole) =>
    apiRequest(
      'POST',
      `/api/projects/${encodeURIComponent(id)}/members`,
      projectMembersResponseSchema,
      { email, role },
    ),
  updateMember: (id: string, userId: string, role: MemberRole) =>
    apiRequest(
      'PATCH',
      `/api/projects/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`,
      projectMembersResponseSchema,
      { role },
    ),
  removeMember: (id: string, userId: string) =>
    apiRequest(
      'DELETE',
      `/api/projects/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`,
      null,
    ),
};
